/**
 * Channel allocation drawer — user-adjusted % split for Maestro follow-up context.
 */

import React from 'react';
import { Box, Button, Divider, Drawer, Slider, Stack, Typography } from '@mui/material';
import { brandColors } from '../../../../theme/orchestrateTheme';
import { normalizeChannelPercents } from '../../../../utils/flightTimelineMerge';

export interface MediaMixerDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Initial channel → percent (0–100). Merged with any keys added via sliders. */
  initialAllocations: Record<string, number>;
  reason?: string;
  onApply: (allocationsPercent: Record<string, number>) => void;
}

const MediaMixerDrawer: React.FC<MediaMixerDrawerProps> = ({
  open,
  onClose,
  initialAllocations,
  reason,
  onApply,
}) => {
  const [allocations, setAllocations] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (open) {
      const keys = Object.keys(initialAllocations);
      setAllocations(
        keys.length
          ? normalizeChannelPercents(initialAllocations)
          : { Digital: 50, TV: 30, Other: 20 }
      );
    }
  }, [open, initialAllocations]);

  const channels = Object.keys(allocations);
  const total = channels.reduce((s, k) => s + (allocations[k] ?? 0), 0);

  const setChannel = (key: string, value: number) => {
    setAllocations((prev) => {
      const next = { ...prev, [key]: value };
      return normalizeChannelPercents(next);
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 2 } }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.deepTeal }}>
        Media mixer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Adjust channel mix (percent). Applied to your next Maestro message as budget context.
      </Typography>
      {reason ? (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          {reason}
        </Typography>
      ) : null}
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {channels.map((ch) => (
          <Box key={ch}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{ch}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {allocations[ch]?.toFixed(1)}%
              </Typography>
            </Stack>
            <Slider
              size="small"
              value={allocations[ch] ?? 0}
              min={0}
              max={100}
              step={0.5}
              onChange={(_, v) => setChannel(ch, v as number)}
            />
          </Box>
        ))}
      </Stack>
      <Typography
        variant="caption"
        color={Math.abs(total - 100) < 0.5 ? 'success.main' : 'warning.main'}
      >
        Total: {total.toFixed(1)}% {Math.abs(total - 100) < 0.5 ? '(balanced)' : ''}
      </Typography>
      <Box sx={{ mt: 'auto', pt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            onApply(normalizeChannelPercents(allocations));
            onClose();
          }}
        >
          Apply to next message
        </Button>
      </Box>
    </Drawer>
  );
};

export default MediaMixerDrawer;
